import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import CookieBanner from "./components/CookieBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { NotificationProvider } from "./context/NotificationContext";
import Layout from "./components/Layout";
import "./App.css";

// Route-level code splitting: each page ships in its own chunk so the
// initial bundle stays small (SEO/performance).
const Register = lazy(() => import("./pages/Register"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const CoachPublicPage = lazy(() => import("./pages/CoachPublicPage"));
const AdminCoachBioPage = lazy(() => import("./pages/AdminCoachBioPage"));
const AdminFreemiumPage = lazy(() => import("./pages/AdminFreemiumPage"));
const AdminLeadsPage = lazy(() => import("./pages/AdminLeadsPage"));
const AdminConsentsPage = lazy(() => import("./pages/AdminConsentsPage"));
const AdminInvoicesPage = lazy(() => import("./pages/AdminInvoicesPage"));
const AdminMessagesPage = lazy(() => import("./pages/AdminMessagesPage"));
const AdminMessageTemplatesPage = lazy(() => import("./pages/AdminMessageTemplatesPage"));
const AdminWeeklyFeedbackPage = lazy(() => import("./pages/AdminWeeklyFeedbackPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const InstagramLandingPage = lazy(() => import("./pages/InstagramLandingPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const WorkoutLog = lazy(() => import("./pages/WorkoutLog"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const PlansPage = lazy(() => import("./pages/PlansPage"));
const ZonesPage = lazy(() => import("./pages/ZonesPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const CustomPlanFormPage = lazy(() => import("./pages/CustomPlanFormPage"));
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CoachLibraryPage = lazy(() => import("./pages/CoachLibraryPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const SchoolPage = lazy(() => import("./pages/SchoolPage"));
const AdminSchoolPage = lazy(() => import("./pages/AdminSchoolPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const LegalNoticePage = lazy(() => import("./pages/LegalNoticePage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="w-8 h-8 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="w-8 h-8 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : user.role === 'school' ? '/school' : '/dashboard'} replace />;
  }
  
  return children;
};

// Redirects admins away from athlete-only pages to /admin
const AthleteOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'school') return <Navigate to="/school" replace />;
  return children;
};

// School members only — everyone else goes to their own panel
const SchoolOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role !== 'school') return <Navigate to="/dashboard" replace />;
  return children;
};

// Strava's OAuth redirect URI is fixed; route school members to their panel
const StravaCallbackRoute = () => {
  const { user } = useAuth();
  if (user?.role === 'school') return <SchoolPage />;
  return <AthleteOnly><ConnectionsPage /></AthleteOnly>;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
          <Toaster 
            position="top-right" 
            richColors 
            theme="dark"
            toastOptions={{
              style: {
                background: '#18181b',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#f4f4f5'
              }
            }}
          />
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
                <div className="w-8 h-8 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin"></div>
              </div>
            }
          >
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/coach/:slug" element={<CoachPublicPage />} />
            <Route path="/ig" element={<InstagramLandingPage />} />
            <Route path="/valoracion" element={<InstagramLandingPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/aviso-legal" element={<LegalNoticePage />} />
            <Route path="/terminos" element={<TermsPage />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<AthleteOnly><Dashboard /></AthleteOnly>} />
              <Route path="/onboarding" element={<AthleteOnly><OnboardingPage /></AthleteOnly>} />
              <Route path="/calendar" element={<AthleteOnly><CalendarPage /></AthleteOnly>} />
              <Route path="/log" element={<AthleteOnly><WorkoutLog /></AthleteOnly>} />
              <Route path="/stats" element={<AthleteOnly><StatsPage /></AthleteOnly>} />
              <Route path="/plans" element={<AthleteOnly><PlansPage /></AthleteOnly>} />
              <Route path="/zones" element={<AthleteOnly><ZonesPage /></AthleteOnly>} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/subscription" element={<AthleteOnly><SubscriptionPage /></AthleteOnly>} />
              <Route path="/custom-plan-form" element={<AthleteOnly><CustomPlanFormPage /></AthleteOnly>} />
              <Route path="/connections" element={<AthleteOnly><ConnectionsPage /></AthleteOnly>} />
              <Route path="/connections/strava/callback" element={<StravaCallbackRoute />} />
              <Route path="/connections/garmin/callback" element={<AthleteOnly><ConnectionsPage /></AthleteOnly>} />
              <Route path="/library" element={<CoachLibraryPage />} />
              <Route path="/school" element={<SchoolOnly><SchoolPage /></SchoolOnly>} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/school" element={<AdminSchoolPage />} />
              <Route path="/admin/coach-bio" element={<AdminCoachBioPage />} />
              <Route path="/admin/freemium" element={<AdminFreemiumPage />} />
              <Route path="/admin/leads" element={<AdminLeadsPage />} />
              <Route path="/admin/consents" element={<AdminConsentsPage />} />
              <Route path="/admin/invoices" element={<AdminInvoicesPage />} />
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/messages/templates" element={<AdminMessageTemplatesPage />} />
              <Route path="/admin/feedback" element={<AdminWeeklyFeedbackPage />} />
              <Route path="/invoices" element={<AdminInvoicesPage />} />
              <Route path="/messages" element={<AthleteOnly><MessagesPage /></AthleteOnly>} />
            </Route>
          </Routes>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
